import { BarChart3, Target, Zap, Clock, Search, Sparkles, BookOpen, ArrowRight, Phone, Mail, CheckCircle } from 'lucide-react';

const STEPS = [
  {
    num: '1',
    icon: Search,
    color: '#3B82F6',
    title: 'Rechercher des prospects',
    tab: 'recherche',
    desc: 'Trouvez des entreprises, CE, hotels, associations dans votre zone. La recherche automatique interroge les Pages Jaunes et enrichit les fiches.',
    tips: ['Ciblez par secteur : entreprise, CE, seminaire, mariage...', 'La zone geographique est pre-configuree autour de Perpignan'],
  },
  {
    num: '2',
    icon: Zap,
    color: '#009893',
    title: 'Mode Rapide — Appeler',
    tab: 'rapide',
    desc: 'Votre outil principal. Cliquez sur un prospect et l\'app vous donne un script d\'appel adapte a son secteur avec les objections possibles.',
    tips: [
      'Apres l\'appel, notez le resultat (interesse, a rappeler, refus)',
      'Le statut passe auto : nouveau → contacte → devis → gagne',
      'Fixez une date de relance si "a rappeler"',
    ],
  },
  {
    num: '3',
    icon: Sparkles,
    color: '#8B5CF6',
    title: 'Assistant IA — Emails & Strategie',
    tab: 'ai-assistant',
    desc: 'Votre coach commercial. Demandez-lui de rediger un email, un script d\'appel, une strategie de conversion. Il connait vos prospects et votre restaurant.',
    tips: [
      'Sans prospect : "Bilan prospection", "Prochaines actions"',
      'Avec prospect : "Email premier contact", "Script appel", "Strategie conversion"',
      'Copiez la reponse en un clic pour l\'envoyer',
    ],
  },
  {
    num: '4',
    icon: Target,
    color: '#F59E0B',
    title: 'Pipeline — Suivre l\'avancement',
    tab: 'pipeline',
    desc: 'Vision Kanban de tous vos prospects par statut. Voyez d\'un coup d\'oeil ou en est chaque prospect.',
    tips: ['Nouveau → Contacte → Devis → Negoce → Gagne', 'Les prospects "chauds" (score IA > 65) apparaissent sur le tableau de bord'],
  },
  {
    num: '5',
    icon: Clock,
    color: '#EF4444',
    title: 'Relances — Ne rien oublier',
    tab: 'relances',
    desc: 'Les prospects a relancer aujourd\'hui. L\'app vous previent automatiquement via Telegram quand c\'est le jour.',
    tips: ['Fixez toujours une date de relance apres un appel', 'Le badge rouge sur l\'onglet indique le nombre de relances en retard'],
  },
  {
    num: '6',
    icon: BookOpen,
    color: '#059669',
    title: 'Entrainer l\'IA',
    tab: 'ia-knowledge',
    desc: 'Plus l\'IA en sait sur vous, meilleures sont ses reponses. Ajoutez votre pitch, vos specialites, vos references clients, votre ton.',
    tips: ['Pitch : qui vous etes, votre histoire', 'References : mariages realises, clients importants', 'Ton : chaleureux, vouvoyer, expressions catalanes...'],
  },
];

const WORKFLOW = [
  { icon: Search, label: 'Rechercher', color: '#3B82F6' },
  { icon: Phone, label: 'Appeler', color: '#009893' },
  { icon: Mail, label: 'Email IA', color: '#8B5CF6' },
  { icon: Target, label: 'Suivre', color: '#F59E0B' },
  { icon: Clock, label: 'Relancer', color: '#EF4444' },
  { icon: CheckCircle, label: 'Gagner', color: '#059669' },
];

export function CrmGuide({ onNavigate }) {
  return (
    <div className="space-y-6" data-testid="crm-guide">
      {/* Header */}
      <div className="text-center py-4">
        <h2 className="text-lg font-bold text-[#1B2A52] tracking-tight">Comment utiliser le CRM</h2>
        <p className="text-xs text-gray-400 mt-1">Votre guide pour convertir des prospects en clients</p>
      </div>

      {/* Workflow visual */}
      <div className="flex items-center justify-between gap-1 px-2 py-4 bg-gray-50 rounded-xl">
        {WORKFLOW.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={i} className="flex items-center gap-1">
              <div className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: step.color + '15' }}>
                  <Icon size={14} style={{ color: step.color }} />
                </div>
                <span className="text-[8px] font-bold text-gray-500 text-center leading-tight">{step.label}</span>
              </div>
              {i < WORKFLOW.length - 1 && <ArrowRight size={12} className="text-gray-300 mx-0.5 mb-3" />}
            </div>
          );
        })}
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {STEPS.map((step) => {
          const Icon = step.icon;
          return (
            <button key={step.num} onClick={() => onNavigate(step.tab)}
              className="w-full text-left bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all group"
              data-testid={`guide-step-${step.num}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: step.color + '12' }}>
                  <Icon size={18} style={{ color: step.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center text-white shrink-0" style={{ backgroundColor: step.color }}>
                      {step.num}
                    </span>
                    <h3 className="text-sm font-bold text-gray-800">{step.title}</h3>
                    <ArrowRight size={12} className="text-gray-300 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{step.desc}</p>
                  <div className="mt-2 space-y-0.5">
                    {step.tips.map((tip, i) => (
                      <p key={i} className="text-[10px] text-gray-400 flex items-start gap-1.5">
                        <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: step.color }} />
                        {tip}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Pro tip */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-xl p-4">
        <p className="text-xs font-bold text-purple-700 mb-1">Astuce Pro</p>
        <p className="text-[11px] text-purple-600 leading-relaxed">
          Commencez chaque journee par l'onglet <b>Relances</b> pour voir qui rappeler. 
          Puis passez en <b>Mode Rapide</b> pour 30 min d'appels. 
          Utilisez l'<b>Assistant IA</b> pour envoyer les emails de suivi. 
          En 1h par jour, vous pouvez contacter 10 prospects !
        </p>
      </div>
    </div>
  );
}
